import { create } from "zustand";
import type { ComponentType } from "react";

export interface AppInfo {
  id: string;
  name: string;
  icon: ComponentType<{ className?: string }>;
  color: string;
  route: string;
}

interface OpenApp {
  appInfo: AppInfo;
  openedAt: number;
  lastRoute: string;
}

interface OpenAppsStore {
  openApps: Map<string, OpenApp>;
  activeAppId: string | null;

  openApp: (app: AppInfo, route?: string) => void;
  closeApp: (appId: string) => void;
  switchApp: (appId: string) => void;
  updateLastRoute: (appId: string, route: string) => void;
  closeAllApps: () => void;
  getOrderedApps: () => OpenApp[];
}

export const useOpenAppsStore = create<OpenAppsStore>((set, get) => ({
  openApps: new Map(),
  activeAppId: null,

  openApp: (app, route) => {
    set((s) => {
      const newMap = new Map(s.openApps);
      if (!newMap.has(app.id)) {
        newMap.set(app.id, {
          appInfo: app,
          openedAt: Date.now(),
          lastRoute: route ?? app.route,
        });
      } else {
        // Update last route if provided
        const existing = newMap.get(app.id)!;
        newMap.set(app.id, {
          ...existing,
          lastRoute: route ?? existing.lastRoute,
        });
      }
      return { openApps: newMap, activeAppId: app.id };
    });
  },

  closeApp: (appId) => {
    set((s) => {
      const newMap = new Map(s.openApps);
      newMap.delete(appId);

      let nextActive = s.activeAppId;
      if (s.activeAppId === appId) {
        // Switch to most recently opened remaining app, or null
        const remaining = Array.from(newMap.values());
        remaining.sort((a, b) => b.openedAt - a.openedAt);
        nextActive =
          remaining.length > 0 ? remaining[0].appInfo.id : null;
      }

      return { openApps: newMap, activeAppId: nextActive };
    });
  },

  switchApp: (appId) => {
    const app = get().openApps.get(appId);
    if (app) {
      set({ activeAppId: appId });
    }
  },

  updateLastRoute: (appId, route) => {
    set((s) => {
      const existing = s.openApps.get(appId);
      if (!existing) return s;
      const newMap = new Map(s.openApps);
      newMap.set(appId, { ...existing, lastRoute: route });
      return { openApps: newMap };
    });
  },

  closeAllApps: () => {
    set({ openApps: new Map(), activeAppId: null });
  },

  getOrderedApps: () => {
    const apps = Array.from(get().openApps.values());
    apps.sort((a, b) => a.openedAt - b.openedAt);
    return apps;
  },
}));
